/**
 * 机构后台 · 考务接口的类型与调用:与 apps/api 的 /v1/admin/exams、/v1/proctor、/v1/admin/org、/v1/content/papers、/v1/admin/accounts 一一对应。
 * 页面只认这里的类型;字段名与后端行一致(tinyint 仍是 0 / 1,由页面转成布尔)。
 */
import type { ExamRegistrationSettings } from './cert.js';
import type { ApiClient } from './client.js';
import type { AcmBoardRow, AcmConfig, AcmPhase, ExamNotice, ProctorPolicy } from './exam.js';

export interface ScoreSet {
  objective: number;
  code: number;
  subjective: number;
  total: number;
  grade: string | null;
  rank: number | null;
  groupSize: number | null;
  passed: boolean | null;
  /** ACM:通过题数与罚时(名次按它排) */
  acm?: { solved: number; penalty: number } | null;
}
/** 监考端 ACM 榜单的一行:实名、记录号、考场 */
export interface AcmStaffRow extends AcmBoardRow {
  recordId: string;
  number: string;
  nickname: string;
  roomId: string | null;
  answerStatus: string;
  reviewVerdict: string;
}
export interface JudgeBacklog {
  queued: number;
  oldestAt: string | null;
  waitMin: number;
  /** 积压 ≥ 5 分钟:ACM 要公告并延长 */
  degraded: boolean;
  suggestMin: number;
}
export interface AcmStaffBoard {
  config: AcmConfig;
  group: string;
  groups: { key: string; label: string }[];
  problems: { id: string; label: string; title: string }[];
  computedAt: string;
  /** 选手现在看到的阶段 */
  phase: AcmPhase;
  freezeAt: string | null;
  unfrozenAt: string | null;
  /** 不能揭榜的原因(还有考场没结束);null = 可以揭 */
  unfreezeBlocker: string | null;
  total: number;
  live: AcmStaffRow[];
  frozen: AcmStaffRow[] | null;
  backlog: JudgeBacklog;
}
export interface BroadcastRow {
  id: string;
  examId: string;
  roomId: string | null;
  kind: string;
  text: string;
  extendMin: number;
  createdBy: string | null;
  createdAt: string;
}
export interface MakeupRule {
  allowed: boolean;
  maxTimes: number;
  sources: string[];
}
export interface AdminExam {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  subject: string;
  nature: string;
  purpose: string;
  /** 默认卷:没单独配卷的组别用它 */
  paperId: string | null;
  /** 按组别配卷:组别 → 试卷 id(只列和默认卷不同的组别;顺带发现 6) */
  groupPapers?: Record<string, string> | null;
  shuffleQuestions: number;
  shuffleOptions: number;
  shuffleCode: number;
  showScores: number;
  /** 开放场次预约(老数据没有这两个字段按不开放 / 48 小时) */
  bookingOpen?: number;
  bookingDeadlineHours?: number;
  contestFormat: string;
  codeAllOrNothing: number;
  /** ACM 参数(只 ACM 有) */
  acm?: AcmConfig | null;
  /** ACM 揭榜时间 */
  boardUnfrozenAt?: string | null;
  durationMin: number;
  lateLimitMin: number;
  passScore: number;
  gradeLines: { S: number; A: number; B: number } | null;
  groups: string[];
  proctor: ProctorPolicy;
  judgeTiming: string;
  subjectiveHours: number;
  appealDays: number;
  publishMode: 'manual' | 'auto' | 'scheduled';
  publishAt: string | null;
  explanationOpen: number;
  makeup: MakeupRule;
  wrongBook: number;
  /** 认证考自主报名设置(只「正式 × 认证考」;空 = 只能导入) */
  registration?: ExamRegistrationSettings | null;
  status: string;
  originalExamId: string | null;
  makeupKind: string | null;
  publishedAt: string | null;
  scorePublishedAt: string | null;
  archivedAt: string | null;
  /** 平台解锁后到这个时间前不自动再归档 */
  unlockedUntil?: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface PrecheckIssue {
  field: string;
  message: string;
  roomId?: string;
}
export interface AdminExamListItem extends AdminExam {
  effectiveStatus: string;
  /** 申诉窗口全部结束的时间(有成绩发布了才有) */
  appealUntil?: string | null;
  roomCount: number;
  startAt: string | null;
  endAt: string | null;
  expected: number;
  submitted: number;
  /** 考试主观题 / Scratch 待批份数与超时份数(教师工作台「考试待批改」) */
  reviewPending?: number;
  reviewOverdue?: number;
  issues: PrecheckIssue[];
  /** 考场缺的时间字段(去重,如「检录开始时间」);有就整行标红写明(B15-3) */
  configMissing?: string[];
}
export interface AdminRoom {
  id: string;
  examId: string;
  group: string;
  seq: number;
  name: string;
  checkinAt: string | null;
  startAt: string | null;
  endAt: string | null;
  extendedMin: number;
  capacity: number;
  chiefProctorId: string | null;
  proctorIds: string[];
  thirdView: 'inherit' | 'on' | 'off';
  /** PC 考试客户端的考场开关(二期) */
  pcClient?: 'inherit' | 'on' | 'off';
  status: string;
}
export interface AdminExamDetail extends AdminExam {
  effectiveStatus: string;
  rooms: (AdminRoom & { imported: number })[];
  issues: PrecheckIssue[];
  /** 只提示、不拦发布(场次容量不够、预约截止后还有人没约) */
  warnings?: PrecheckIssue[];
  counts: {
    records: number;
    checkedIn: number;
    answering: number;
    submitted: number;
    graded: number;
    /** 开放预约的考试:还没落座(没约场次)的人;不开预约的 = 还没分到考场的人 */
    unbooked?: number;
  };
  /** 申诉窗口全部结束的时间 */
  appealUntil?: string | null;
  /** 开考后锁结构的原因(null = 还能导入 / 建考场 / 改考场 / 改监考策略) */
  lock?: string | null;
  /** 能不能归档、为什么不能(按钮置灰写原因) */
  archiveCheck?: { ok: boolean; reasons: string[] };
  /** 能不能删(只有草稿) */
  deletable?: boolean;
  /** 单个考场容量上限(规则「单个考场容量上限」,没配按 5000) */
  roomCapacityMax?: number;
}
/** 发布前检查(10-02 闭环):多场次、评测失败、主观题待批、待复核 */
export interface PublishCheck {
  status: string;
  publishMode: string;
  publishAt: string | null;
  /** 还没结束的考场数、在答人数、还要等的人数、缺考人数 */
  roomsOpen: number;
  answering: number;
  waiting: number;
  absent: number;
  /** 能发的(考场已结束且已交卷 / 代交)、其中已发 / 未发、未发里还没出分的 */
  ready: number;
  published: number;
  unpublished: number;
  notGraded: number;
  /** 评测失败(含按 0 分判掉的 Scratch 老数据)· 其中 Scratch · 还在评测队列 */
  judgeFailed: number;
  scratchZero: number;
  judging: number;
  /** 主观题 / Scratch 待批份数、待复核人数 */
  subjective: number;
  held: number;
  /** 全部考场结束且没人在答 */
  complete: boolean;
  rooms: { id: string; name: string; endAt: string | null; ended: boolean }[];
}
/** 延长本场的申请(累计超 60 分钟要平台审批) */
export interface ExtendRequest {
  id: string;
  examId: string;
  roomId: string | null;
  minutes: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  decisionNote: string | null;
  decidedAt: string | null;
  createdAt: string;
}
/** 平台 · 延长审批列表的一行 */
export interface PlatformExtendRequest extends Omit<ExtendRequest, 'examId' | 'roomId'> {
  requester: string;
  tenantName: string;
  exam: { id: string; name: string };
  room: string;
  /** 已累计延长(分钟)与现在的结束时间 */
  extendedMin: number;
  endAt: string | null;
}
/** 平台 · 已归档考试 */
export interface ArchivedExam {
  id: string;
  code: string;
  name: string;
  nature: string;
  purpose: string;
  tenantId: string;
  tenantName: string;
  archivedAt: string | null;
  scorePublishedAt: string | null;
}
/** 两人确认的第二确认人(手机号脱敏) */
export interface Confirmer {
  id: string;
  name: string;
  phone: string;
  chief: boolean;
  roles: string[];
}
/** 场次预约情况(B18):按组别列场次、截止时间、自约 / 机构分配 / 没约的人数 */
export interface BookingOverview {
  open: boolean;
  deadlineHours: number;
  groups: {
    group: string;
    deadline: string | null;
    total: number;
    booked: number;
    assigned: number;
    unbooked: number;
    sessions: {
      key: string;
      checkinAt: string | null;
      startAt: string;
      endAt: string | null;
      roomIds: string[];
      capacity: number;
      booked: number;
      remaining: number;
    }[];
  }[];
}
export interface AdminRecord {
  /** PC 考试客户端(二期):状态(空 = 没用过客户端)、最近上报、明细 */
  pcClientState?: string | null;
  pcClientSeenAt?: string | null;
  pcClient?: {
    version?: string;
    device?: string;
    os?: string;
    displays?: number;
    remote?: boolean;
    vm?: string;
    blocked?: number;
    browserPass?: { at: string; reason: string } | null;
  } | null;
  id: string;
  examId: string;
  roomId: string | null;
  group: string;
  studentId: string;
  number: string;
  registrationNumber: string;
  source: string;
  originalRecordId: string | null;
  checkinStatus: string;
  answerStatus: string;
  submitMode: string | null;
  checkedInAt: string | null;
  startedAt: string | null;
  submittedAt: string | null;
  deadlineAt: string | null;
  extraMin: number;
  tabSwitches: number;
  anomalyCount: number;
  reviewVerdict: string;
  scores: ScoreSet | null;
  scoreStatus: string;
  finalRule: string | null;
  finalScore: string | null;
  gradedAt: string | null;
  scorePublishedAt: string | null;
  appealCount: number;
  /** 本场影像授权快照:1 有 / 0 没有(本场人工核验、不抓拍不录屏不开第三视角)/ 空 = 还没检录 */
  mediaConsent?: number | null;
  student: { id: string; name: string; nickname: string } | null;
  pendingEvents: number;
  /** 抓拍中断过(摄像头断开 / 打不开,或交卷时检出长时间没有抓拍;A11-3,只标记) */
  captureInterrupted?: boolean;
}
export interface ProctorEvent {
  id: string;
  examId: string;
  roomId: string | null;
  recordId: string;
  source: string;
  kind: string;
  at: string;
  mediaKey: string | null;
  mediaValid: number | null;
  /** 平台影像清理处理过:masked 已打码(原件封存)· sealed 只封存(打不开) */
  mediaState?: 'masked' | 'sealed' | null;
  payload: Record<string, unknown> | null;
  verdict: string;
  handledBy: string | null;
  handledAt: string | null;
  note: string | null;
  record?: { number: string; answerStatus: string; tabSwitches: number; reviewVerdict: string } | null;
  student?: { id: string; name: string; nickname: string } | null;
}
export interface DashboardRoom {
  room: {
    id: string;
    name: string;
    group: string;
    checkinAt: string | null;
    startAt: string | null;
    endAt: string | null;
    extendedMin: number;
    /** 主监考(也能撤销强制交卷 / 取消资格) */
    chiefProctorId?: string | null;
    thirdView: string;
    /** 这个考场要不要用 PC 考试客户端:off / mark / hold_score(二期) */
    pcClient?: string;
  };
  expected: number;
  checkedIn: number;
  answering: number;
  submitted: number;
  absent: number;
  pendingEvents: number;
}
export interface Dashboard {
  exam: {
    id: string;
    name: string;
    status: string;
    lateLimitMin: number;
    proctor: ProctorPolicy;
    contestFormat?: string;
  };
  serverTime: string;
  /** 本场判题积压(ACM ≥ 5 分钟要公告并延长) */
  judgeBacklog?: JudgeBacklog;
  /** 最近 10 条考中公告 */
  broadcasts?: BroadcastRow[];
  /** 延长本场的申请(累计超 60 分钟要平台审批):待审批 / 最近处理的 */
  extendRequests?: ExtendRequest[];
  rooms: DashboardRoom[];
  totals: {
    expected: number;
    checkedIn: number;
    answering: number;
    submitted: number;
    pendingEvents: number;
  };
  judgeQueue: { queued: number; running: number; failed: number };
  recent: ProctorEvent[];
}
// ── 整题重判 ──
export interface FixCase {
  input: string;
  expected: string;
  score: number;
  sample: boolean;
}
export interface FixJudge {
  testCases: FixCase[];
  compareModes: string[];
  timeLimitMs: number;
  memoryLimitMb: number;
}
/** 勘误口径:编程题 judge(+ 可选正确标程,只用于自检);客观题 answer(多选可带判分方式) */
export interface FixSpec {
  judge?: FixJudge;
  solution?: { code: string };
  answer?: unknown;
  multiScoring?: string | null;
}
export interface QuestionFix {
  id: string;
  questionId: string;
  label: string;
  seq: number;
  kind: 'cases' | 'answer';
  reason: string;
  /** checking 标程自检中 · check_failed 自检没过 · running 重判中 · done 完成 */
  status: 'checking' | 'check_failed' | 'running' | 'done';
  total: number;
  pending: number;
  failed: number;
  changed: number;
  up: number;
  down: number;
  checkResult: {
    passed: number;
    total: number;
    errorType: string | null;
    cases: { no: number; status: string; stdout?: string; expected?: string; error?: string }[];
  } | null;
  createdAt: string;
  finishedAt: string | null;
}
export interface ExamQuestionsView {
  exam: { status: string; published: boolean; acm: boolean };
  items: {
    questionId: string;
    label: string;
    type: string;
    maxScore: number;
    answered: number;
    rate: number | null;
    zeroRate: number | null;
    fixKind: 'cases' | 'answer' | null;
    fixes: QuestionFix[];
  }[];
}
export interface FixSpecView {
  questionId: string;
  label: string;
  type: string;
  kind: 'cases' | 'answer' | null;
  stem: string;
  options: { key: string; text: string }[] | null;
  maxScore: number;
  spec: FixSpec;
  fixSeq: number;
  hasSolution: boolean;
  acm: boolean;
  published: boolean;
}
export interface FixPreview {
  kind: 'cases' | 'answer';
  label: string;
  records: number;
  submissions: number;
  published: boolean;
  acm: boolean;
  changes: {
    up: number;
    down: number;
    sample: { recordId: string; number: string; name: string; before: number; after: number }[];
  } | null;
}
export const FIX_STATUS_LABELS: Record<string, [string, string]> = {
  checking: ['标程自检中', 'brand'],
  check_failed: ['自检没过 · 未重判', 'bad'],
  running: ['重判中', 'warn'],
  done: ['已完成', 'ok'],
};
export interface ScreenClip {
  id: string;
  seq: number | null;
  from: string;
  to: string;
  sec: number;
  bytes: number | null;
  /** 已脱敏封存的片段没有 key */
  mediaKey: string | null;
  mediaState: string | null;
}
export interface RecordArchive {
  record: AdminRecord;
  student: { id: string; name: string; nickname: string } | null;
  room?: { id: string; name: string; chiefProctorId: string | null; endAt: string | null } | null;
  /** 现在能不能强制交卷 / 取消资格(考试进行中,含检录) */
  live?: boolean;
  summary: {
    checkinPhotos: number;
    /** 本平台定时抓拍(不含第三视角拍照、第三方回传) */
    captures: number;
    tabSwitches: number;
    /** 第三视角的全部记录(接入 / 断开 / 拍照 / 录像 / 异常) */
    thirdView: number;
    /** 第三视角手机拍的照片、第三方承办平台回传的图片(B8-2:和本平台抓拍同一页分开看) */
    thirdViewPhotos?: number;
    externalPhotos?: number;
    aiFlags: number;
    /** PC 考试客户端的记录条数(握手 / 锁定 / 拦下的切换 / 禁用程序 / 环境异常) */
    pcClient?: number;
    /** 抓拍中断次数(A11-3) */
    captureInterrupted?: number;
    pending: number;
    versions: number;
    /** 低码率录屏:片段数与总秒数 */
    screenClips: number;
    screenSec: number;
  };
  events: ProctorEvent[];
  answers: {
    id: string;
    questionId: string;
    version: number;
    savedAt: string;
    status: string;
    score: string | null;
    submitNo: number;
  }[];
}
export interface Appeal {
  id: string;
  examId: string;
  recordId: string;
  number: string;
  questionId: string | null;
  kind: string;
  text: string;
  attachments: string[] | null;
  status: string;
  verdictNote: string | null;
  handledBy: string | null;
  handledAt: string | null;
  scoreBefore: ScoreSet | null;
  scoreAfter: ScoreSet | null;
  anchor: string | null;
  createdAt: string;
  /** 挂起超过 2 天没处理(学生端承诺 3 个工作日内答复) */
  overdue?: boolean;
}
export interface ScoreChange {
  id: string;
  examId: string;
  recordId: string;
  before: ScoreSet | null;
  after: ScoreSet;
  reason: string;
  anchor: string;
  appealId: string | null;
  createdBy: string | null;
  createdAt: string;
}
export interface ReviewItem {
  id: string;
  recordId: string;
  questionId: string;
  version: number;
  answer: unknown;
  savedAt: string;
  /** 出分截止(交卷 + 本场主观题出分时限)、是否超时、还剩几小时 */
  dueAt?: string;
  overdue?: boolean;
  hoursLeft?: number;
  question: { type: string; stem: string; maxScore: number; explanation: string | null } | null;
  record: { number: string; group: string; submittedAt: string | null } | null;
  student: { id: string; name: string; nickname: string } | null;
  /** AI 建议分与理由(只给老师看,学生端不出;J-1) */
  aiSuggestion?: { score: number; comment: string } | null;
  /** 不能「当场生成建议分」的原因(没配 AI 服务 / Scratch 工程不发给 AI);null = 能 */
  aiUnavailable?: string | null;
  /** 后台自动生成过、没给出建议分(可以再点「当场生成」) */
  aiMissed?: boolean;
}
export interface Paper {
  id: string;
  tenantId: string;
  name: string;
  /** fixed = 固定卷;rule = 抽题规则(不能直接用于考试) */
  kind?: string;
  version?: number;
  questionIds: string[];
  totalScore: number;
  durationMin: number;
  status: string;
  createdAt: string;
}
export interface OrgClass {
  id: string;
  name: string;
  grade: string | null;
  tags: string[] | null;
  status: string;
  memberCount: number;
}
export interface OrgStudent {
  id: string;
  name: string;
  nickname: string;
  birthMonth: string;
  schoolStage: string;
  grade: string | null;
  school: string | null;
  status: string;
}
export interface StaffAccount {
  id: string;
  name: string;
  phone: string;
  siteRoles: string[];
  lastLoginAt: string | null;
}

/** 建考 / 改考的输入(与后端 ExamSchema 一致;布尔在这里是真布尔) */
export interface ExamInput {
  name: string;
  subject: string;
  nature: string;
  purpose: string;
  paperId?: string;
  /** 按组别配卷:组别 → 试卷 id(只配和默认卷不同的;null = 全部用默认卷) */
  groupPapers?: Record<string, string> | null;
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
  shuffleCode: boolean;
  showScores: boolean;
  bookingOpen: boolean;
  bookingDeadlineHours: number;
  contestFormat: string;
  codeAllOrNothing: boolean;
  /** ACM 参数(赛制 = acm 时传) */
  acm?: AcmConfig | null;
  durationMin: number;
  lateLimitMin: number;
  passScore: number;
  gradeLines?: { S: number; A: number; B: number };
  groups: string[];
  proctor: ProctorPolicy;
  judgeTiming: string;
  subjectiveHours: number;
  appealDays: number;
  publishMode: 'manual' | 'auto' | 'scheduled';
  publishAt?: string;
  explanationOpen: boolean;
  makeup: MakeupRule;
  wrongBook: boolean;
  registration?: ExamRegistrationSettings | null;
}
export interface RoomInput {
  group: string;
  checkinAt?: string;
  startAt?: string;
  endAt?: string;
  capacity: number;
  chiefProctorId?: string;
  proctorIds: string[];
  thirdView: 'inherit' | 'on' | 'off';
  /** PC 考试客户端的考场开关(二期):跟随考试 / 本考场必须用 / 本考场不用 */
  pcClient?: 'inherit' | 'on' | 'off';
  /** 按考场关闭低码率录屏:给理由 = 本考场不录(留痕),null = 跟随考试;不传 = 不改 */
  screenRecordOff?: string | null;
}
export interface AssignInput {
  roomId?: string;
  studentIds?: string[];
  classId?: string;
}
export interface AssignResult {
  created: number;
  skipped: number;
  errors: { studentId: string; message: string }[];
}
export interface MakeupInput {
  kind: 'makeup_score' | 'retake_incident';
  paperId: string;
  name?: string;
  room: Omit<RoomInput, 'group'>;
  candidates: 'system' | string[];
}
export interface PublishScoresResult {
  published: number;
  pendingReview: number;
  unpublished: number;
  /** 还要等的(考场没结束或还在答题)、还没结束的考场;这次把考试转成了「成绩已发布」 */
  waiting?: number;
  roomsOpen?: number;
  completed?: boolean;
}

export const EXAM_STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  published: '已发布',
  checkin: '检录中',
  in_progress: '进行中',
  grading: '判分中',
  pending_review: '待复核',
  score_published: '成绩已发布',
  appeal_period: '申诉期',
  archived: '已归档',
  incident_retake: '故障重考',
};
/** 状态流(S-1):列表页与详情页的进度条按这个顺序画 */
export const STATUS_FLOW = [
  'draft',
  'published',
  'checkin',
  'in_progress',
  'grading',
  'score_published',
  'archived',
] as const;
export const STATUS_TONE: Record<string, string> = {
  draft: '',
  published: 'brand',
  checkin: 'brand',
  in_progress: 'ok',
  grading: 'warn',
  pending_review: 'warn',
  score_published: 'ok',
  appeal_period: 'ok',
  archived: '',
};
export const GROUP_LABELS: Record<string, string> = {
  primary_low: '小学低年级组',
  primary_high: '小学高年级组',
  junior: '初中组',
  senior: '高中组',
};
export const GROUP_KEYS = ['primary_low', 'primary_high', 'junior', 'senior'] as const;
export const CHECKIN_LABELS: Record<string, string> = {
  not_checked_in: '未检录',
  checked_in: '已检录',
  manual_released: '人工放行',
  first_capture: '首采未比对',
};
export const ANSWER_LABELS: Record<string, string> = {
  not_started: '未开始',
  answering: '答题中',
  taken_over: '多端接管',
  submitted: '已交卷',
  auto_submitted: '系统代交',
  force_submitted: '强制交卷',
  disqualified: '取消资格',
};
export const SCORE_STATUS_LABELS: Record<string, string> = {
  not_out: '未出分',
  pending_review: '待审核',
  published: '已发布',
  appealing: '申诉中',
  revised: '已改分',
};
export const SOURCE_LABELS: Record<string, string> = {
  first: '首考',
  makeup_score: '成绩补考',
  retake_incident: '故障重考',
  external_import: '外部承办灌入',
};
export const EVENT_KIND_LABELS: Record<string, string> = {
  tab_switch: '切屏',
  tab_switch_exceeded: '离开页面超阈值',
  capture: '定时抓拍',
  capture_failed: '抓拍上传失败',
  capture_interrupted: '抓拍中断',
  checkin_photo: '检录照',
  face_mismatch: '检录人脸不一致',
  face_not_detected: '检录照无效(没拍到人脸等,已提示重拍)',
  face_compare_unavailable: '人脸比对服务不可用(改人工核验)',
  screen_clip: '低码率录屏片段',
  screen_record_failed: '录屏失败',
  screen_share_stopped: '屏幕共享停止',
  third_view_connected: '第三视角接入',
  third_view_disconnected: '第三视角断开',
  third_view_missing: '未接入第二机位',
  third_view_dark: '第三视角遮挡',
  third_view_static: '第三视角画面无变化',
  third_view_invalid: '第三视角连续无效画面',
  third_view_ended_early: '第三视角提前结束',
  third_view_device_changed: '第三视角换手机',
  clip: '第三视角录像',
  leave_seat: '离座',
  left_seat: '离座',
  paste_blocked: '外部粘贴被拦',
  multi_person: '画面多人',
  no_face: '无人脸',
  adult_face: '成年人面孔(可疑)',
  manual_release_request: '人工放行申请',
  no_consent_check: '未授权影像 · 人工核验',
  manual_release: '人工放行',
  manual_release_rejected: '拒绝人工放行',
  force_submit: '强制交卷',
  disqualify: '取消资格',
  revoke: '撤销处理',
  violation_handled: '违纪处理(记 0 分)',
  makeup_request: '申请补考',
  heartbeat: '心跳',
  takeover: '换设备接管',
  // PC 考试客户端(二期;服务端 exam/domain/pc-client.ts 的 PC_ANOMALY_KINDS / PC_INFO_KINDS)
  pc_client_start: '考试客户端启动',
  pc_client_restart: '考试客户端重新进入',
  pc_locked: '客户端进入锁定',
  pc_unlocked: '客户端解除锁定',
  pc_switch_blocked: '切换程序被拦下',
  pc_focus_lost: '客户端窗口失去焦点',
  pc_forbidden_process: '发现禁用程序',
  pc_vm_detected: '在虚拟机里运行',
  pc_remote_session: '远程桌面会话',
  pc_multi_display: '接了多个显示器',
  pc_lock_failed: '客户端锁定不完整',
  pc_client_lost: '客户端状态上报中断',
  pc_exit_by_code: '用监考口令退出客户端',
  pc_exit_code_wrong: '退出口令输错',
  pc_page_crashed: '客户端里的考试页面崩溃',
  pc_browser_request: '申请改用浏览器',
  pc_browser_pass: '放行改用浏览器',
  pc_browser_reject: '不同意改用浏览器',
  pc_remote_unlock: '监考员远程解锁',
  pc_client_unavailable: '客户端不可用 · 按浏览器模式进行',
};
/** 考试客户端事件的补充说明(事件的 payload → 一句话):监考台事件流、考生档案时间线用 */
export function pcEventDetail(e: { kind: string; payload?: Record<string, unknown> | null }): string {
  const p = e.payload ?? {};
  const s = (k: string) => (typeof p[k] === 'string' || typeof p[k] === 'number' ? String(p[k]) : '');
  switch (e.kind) {
    case 'pc_client_start':
    case 'pc_client_restart':
      return [
        s('version') && `版本 ${s('version')}`,
        s('device') && `设备 ${s('device')}`,
        s('os'),
        p['deviceChanged'] ? '换了一台电脑' : '',
      ]
        .filter(Boolean)
        .join(' · ');
    case 'pc_switch_blocked':
      return `${s('combo')} · 已拦截 · 累计第 ${s('n')} 次`;
    case 'pc_focus_lost':
      return `累计第 ${s('n')} 次`;
    case 'pc_forbidden_process':
      return `${s('name')}${s('group') ? `(${s('group')})` : ''} · ${
        { closed: '已关闭', close_failed: '没关掉', marked: '只记录' }[s('action')] ?? ''
      }`;
    case 'pc_vm_detected':
      return Array.isArray(p['signals']) ? p['signals'].join('、') : '';
    case 'pc_multi_display':
      return `${s('displays')} 个显示器`;
    case 'pc_lock_failed':
      return `${{ keyguard: '切换键拦截', kiosk: '全屏锁定', topmost: '窗口置顶', display_cover: '副屏遮挡' }[s('part')] ?? s('part')}没有生效`;
    case 'pc_client_lost':
      return `中断约 ${Math.round(Number(p['gapSec'] ?? 0) / 60)} 分钟${p['foundAt'] === 'submit' ? '(交卷时发现)' : ''}`;
    case 'pc_unlocked':
      return (
        {
          submitted: '交卷后自动解锁',
          stopped: '被交卷 / 取消资格后解锁',
          proctor: '监考员远程解锁',
          exit_code: '口令退出',
          deadline: '考试时间已过自动解锁',
          left_page: '离开了答题页',
          browser_submit: '在浏览器里直接交卷',
        }[s('reason')] ?? s('reason')
      );
    case 'pc_browser_request':
    case 'pc_browser_pass':
    case 'pc_browser_reject':
    case 'pc_remote_unlock':
      return s('reason');
    default:
      return '';
  }
}
/**
 * 监考记录的叫法(B8-2):同是 kind = capture,第三视角手机拍的写「第三视角拍照」、第三方承办平台回传的加前缀,
 * 别和本平台定时抓拍混成一个名字(106 教训:两处来源分流后查不到)
 */
export function eventLabel(e: { source: string; kind: string }): string {
  if (e.source === 'third_view' && e.kind === 'capture') return '第三视角拍照';
  const base = EVENT_KIND_LABELS[e.kind] ?? e.kind;
  return e.source === 'external' ? `第三方回传 · ${base}` : base;
}
export const EVENT_TONE: Record<string, string> = {
  multi_person: 'bad',
  no_face: 'bad',
  adult_face: 'bad',
  face_mismatch: 'bad',
  tab_switch_exceeded: 'warn',
  tab_switch: 'warn',
  capture_interrupted: 'warn',
  leave_seat: 'warn',
  left_seat: 'warn',
  paste_blocked: 'warn',
  manual_release_request: 'brand',
  no_consent_check: 'brand',
  makeup_request: 'brand',
  pc_browser_request: 'brand',
  pc_forbidden_process: 'warn',
  pc_vm_detected: 'bad',
  pc_remote_session: 'bad',
  pc_multi_display: 'warn',
  pc_lock_failed: 'warn',
  pc_client_lost: 'warn',
  pc_switch_blocked: 'warn',
  pc_focus_lost: 'warn',
  violation_handled: 'bad',
  third_view_disconnected: 'warn',
  third_view_missing: 'warn',
  third_view_dark: 'warn',
  third_view_static: 'warn',
  third_view_invalid: 'warn',
  third_view_ended_early: 'warn',
  force_submit: 'bad',
  disqualify: 'bad',
};
export const VERDICT_LABELS: Record<string, string> = {
  none: '无需处理',
  pending: '待复核',
  normal: '无违纪',
  violation: '违纪',
};
export const CONTEST_FORMAT_LABELS: Record<string, string> = {
  score: '得分制(按用例比例给分)',
  oi: 'OI 赛制(考中不返回结果)',
  acm: 'ACM 赛制(即时反馈 + 罚时)',
  grade: '等级制(总分映射等级)',
};
export const JUDGE_TIMING_LABELS: Record<string, string> = {
  realtime: '考中即时反馈',
  on_submit: '交卷即判',
  batch_after: '考后批量',
};
export const PUBLISH_MODE_LABELS: Record<string, string> = {
  manual: '考后手动发布',
  auto: '出分即自动发布',
  scheduled: '定时发布',
};
export const THIRD_VIEW_ADMIN_LABELS: Record<string, string> = {
  off: '关闭',
  mark: '开启 · 不完整则标记',
  hold_score: '开启 · 不完整则成绩待审核',
};
/** 监考规则一句话(建考表单、补考页「沿用原考试」共用) */
export function proctorSummaryOf(p: ProctorPolicy): string {
  return [
    p.checkin ? `检录${p.faceCompare ? ' + 人脸' : ''}` : '不检录',
    p.captureIntervalSec ? `抓拍 ${p.captureIntervalSec}s` : '不抓拍',
    p.screenRecord ? '录屏' : '不录屏',
    p.aiDetect ? 'AI 识别' : '',
    `第三视角 ${THIRD_VIEW_ADMIN_LABELS[p.thirdView]?.split(' · ')[0] ?? ''}`,
    p.mic ? '麦克风' : '',
  ]
    .filter(Boolean)
    .join(' · ');
}
export const ROOM_THIRD_VIEW_LABELS: Record<string, string> = {
  inherit: '跟随考试',
  on: '本场开启',
  off: '本场关闭',
};
/** 考试规则里「补考资格来源」的三个选项(建考表单、考试概览共用) */
export const MAKEUP_RULE_SOURCE_LABELS: Record<string, string> = {
  system: '系统筛选',
  student: '学生申请',
  admin: '管理员指定',
};
export const MAKEUP_KIND_LABELS: Record<string, string> = {
  makeup_score: '成绩补考(取高分)',
  retake_incident: '故障重考(以重考为准)',
};
export const APPEAL_KIND_LABELS: Record<string, string> = {
  score: '成绩',
  proctor: '监考',
  incident: '故障',
};
/** 延长申请的状态 */
export const EXTEND_STATUS_LABELS: Record<string, [string, string]> = {
  pending: ['待平台审批', 'warn'],
  approved: ['已通过 · 已生效', 'ok'],
  rejected: ['已驳回', 'bad'],
};
export const SUBJECT_PRESETS = ['Scratch 图形化', 'Python', 'C++'];
export const STAFF_ROLES = [
  'org_admin',
  'org_finance',
  'organizer',
  'teacher',
  'exam_admin',
  'proctor',
  'question_author',
  'channel_l1',
  'channel_l2',
  'platform_admin',
  'platform_ops',
  'platform_finance',
  'platform_content',
  'platform_audit',
];
/** 能建 / 改任何性质与用途考试的角色(和后端 EXAM_MANAGER_ROLES 一致);其余(教师)只能建「练习 × 班级测」(B7-3) */
export const EXAM_MANAGER_ROLES = ['org_admin', 'exam_admin', 'organizer', 'platform_admin'];
/** 能进「赛事」后台的角色 */
export const CONTEST_ROLES = ['organizer', 'org_admin', 'platform_admin', 'platform_ops'];
/** 能进「考务」的角色(合作机构、财务不进考务) */
export const EXAM_ROLES = ['org_admin', 'teacher', 'exam_admin', 'proctor', 'platform_admin', 'platform_ops'];
/** 合作机构后台 */
export const CHANNEL_ROLES = ['channel_l1', 'channel_l2'];
/** 卖课程包的一方(机构卖自己的;平台站点 = 平台课程包) */
export const SELLER_ROLES = ['org_admin', 'org_finance', 'platform_admin', 'platform_finance'];
/** 平台 · 渠道与结算 */
export const PLATFORM_FIN_ROLES = ['platform_admin', 'platform_finance', 'platform_ops'];
export const ROLE_LABELS: Record<string, string> = {
  org_admin: '机构管理员',
  org_finance: '机构财务',
  organizer: '主办方管理员',
  teacher: '教师',
  exam_admin: '考试管理员',
  proctor: '监考员',
  question_author: '出题人',
  reviewer: '评审员',
  channel_l1: '一级合作机构',
  channel_l2: '二级合作机构',
  platform_admin: '平台管理员',
  platform_ops: '平台运营',
  platform_finance: '平台财务',
  platform_content: '平台内容',
  platform_support: '平台客服',
  platform_audit: '平台审计(只读)',
  public_school: '公立学校',
  judge: '裁判',
  student: '学生',
  parent: '家长',
};

export function adminApi(api: ApiClient) {
  const b = '/v1/admin/exams';
  const p = '/v1/proctor';
  return {
    exams: {
      list: (q?: { status?: string; purpose?: string }) =>
        api.get<AdminExamListItem[]>(b, { query: { status: q?.status, purpose: q?.purpose } }),
      create: (body: ExamInput) => api.post<AdminExamDetail>(b, body),
      get: (id: string) => api.get<AdminExamDetail>(`${b}/${id}`),
      update: (id: string, patch: Partial<ExamInput>) => api.patch<AdminExamDetail>(`${b}/${id}`, patch),
      publish: (id: string) => api.post<AdminExamDetail>(`${b}/${id}/publish`),
      /** 已发布考试换某个组别的卷(该组别还没人检录才行;二次确认 + 理由) */
      swapGroupPaper: (id: string, body: { group: string; paperId: string; reason: string; confirm: true }) =>
        api.post<AdminExamDetail>(`${b}/${id}/group-papers/swap`, body),
      unpublish: (id: string) => api.post<AdminExamDetail>(`${b}/${id}/unpublish`),
      archive: (id: string) => api.post<AdminExamDetail>(`${b}/${id}/archive`),
      /** 删除草稿考试 / 开考前的空考场 / 没检录没作答的选手(留痕) */
      remove: (id: string) => api.delete<{ id: string; deleted: true }>(`${b}/${id}`),
      removeRoom: (roomId: string) => api.delete<{ id: string; deleted: true }>(`${b}/rooms/${roomId}`),
      removeRecord: (recordId: string) =>
        api.delete<{ id: string; deleted: true }>(`${b}/records/${recordId}`),
      createRoom: (id: string, body: RoomInput) => api.post<AdminRoom>(`${b}/${id}/rooms`, body),
      updateRoom: (roomId: string, patch: Partial<RoomInput>) =>
        api.patch<AdminRoom>(`${b}/rooms/${roomId}`, patch),
      assign: (id: string, body: AssignInput) => api.post<AssignResult>(`${b}/${id}/assign`, body),
      move: (recordId: string, body: { roomId: string; reason: string }) =>
        api.post<AdminRecord>(`${b}/records/${recordId}/move`, body),
      records: (id: string, roomId?: string) =>
        api.get<AdminRecord[]>(`${b}/${id}/records`, { query: { roomId } }),
      booking: (id: string) => api.get<BookingOverview>(`${b}/${id}/booking`),
      autoAssign: (id: string) =>
        api.post<{ assigned: number; left: number }>(`${b}/${id}/booking/auto-assign`),
      reviewQueue: (id: string) => api.get<ReviewItem[]>(`${b}/${id}/review-queue`),
      /** 出分;adoptAi = 一键采用 AI 建议分(分数须等于建议分;仍记为该老师确认、留痕) */
      grade: (answerId: string, body: { score: number; feedback?: string; adoptAi?: boolean }) =>
        api.post<{ id: string }>(`${b}/answers/${answerId}/grade`, body),
      /** 当场生成 AI 建议分(没配 AI 服务 / Scratch 回原因) */
      aiSuggest: (answerId: string) =>
        api.post<{ score: number; comment: string }>(`${b}/answers/${answerId}/ai-suggest`),
      /** 超过出分时限没批的批量采用 AI 建议分;不给 answerIds = 本场队列里全部符合的 */
      adoptAi: (id: string, answerIds?: string[]) =>
        api.post<{ adopted: number; skipped: { id: string; reason: string }[] }>(
          `${b}/${id}/review/adopt-ai`,
          answerIds?.length ? { answerIds } : {},
        ),
      publishScores: (id: string, roomIds?: string[]) =>
        api.post<PublishScoresResult>(`${b}/${id}/scores/publish`, roomIds?.length ? { roomIds } : {}),
      /** 发布前检查:考场 / 在答 / 能发 / 没出分(主观题待批 · 评测中 · 评测失败)/ 待复核 */
      publishCheck: (id: string) => api.get<PublishCheck>(`${b}/${id}/publish-check`),
      /** 重跑失败评测(换新任务号;Scratch 老数据转人工批改) */
      retryFailed: (id: string) =>
        api.post<{ requeued: number; toManual: number }>(`${b}/${id}/judge/retry-failed`),
      /** 违纪处理(成绩出来后查实违纪:改分为 0 + 违纪标记) */
      violation: (recordId: string, reason: string) =>
        api.post<ScoreSet>(`${b}/records/${recordId}/violation`, { reason }),
      appeals: (id: string) => api.get<Appeal[]>(`${b}/${id}/appeals`),
      handleAppeal: (
        appealId: string,
        body: {
          verdict: 'upheld' | 'rejected';
          note: string;
          objective?: number;
          code?: number;
          subjective?: number;
          acmSolved?: number;
          acmPenalty?: number;
        },
      ) => api.post<Appeal>(`${b}/appeals/${appealId}/handle`, body),
      changeScore: (
        recordId: string,
        body: {
          objective?: number;
          code?: number;
          subjective?: number;
          /** ACM:改判后的通过题数 / 罚时 */
          acmSolved?: number;
          acmPenalty?: number;
          reason: string;
        },
      ) => api.post<ScoreSet>(`${b}/records/${recordId}/score-change`, body),
      scoreChanges: (id: string) => api.get<ScoreChange[]>(`${b}/${id}/score-changes`),
      // ── 整题重判 ──
      questions: (id: string) => api.get<ExamQuestionsView>(`${b}/${id}/questions`),
      fixSpec: (id: string, questionId: string) =>
        api.get<FixSpecView>(`${b}/${id}/questions/${questionId}/fix-spec`),
      fixPreview: (id: string, questionId: string, spec: FixSpec) =>
        api.post<FixPreview>(`${b}/${id}/questions/${questionId}/fix/preview`, { spec }),
      fix: (id: string, questionId: string, body: { spec: FixSpec; reason: string }) =>
        api.post<QuestionFix>(`${b}/${id}/questions/${questionId}/fix`, body),
      fixes: (id: string) => api.get<QuestionFix[]>(`${b}/${id}/fixes`),
      retryFix: (fixId: string) => api.post<QuestionFix>(`${b}/fixes/${fixId}/retry`),
    },
    proctor: {
      dashboard: (id: string) => api.get<Dashboard>(`${p}/exams/${id}/dashboard`),
      events: (id: string, q?: { verdict?: string; roomId?: string }) =>
        api.get<ProctorEvent[]>(`${p}/exams/${id}/events`, {
          query: { verdict: q?.verdict, roomId: q?.roomId },
        }),
      handle: (eventId: string, body: { verdict: 'normal' | 'violation'; note?: string }) =>
        api.post<ProctorEvent>(`${p}/events/${eventId}/handle`, body),
      release: (recordId: string, reason?: string) =>
        api.post<AdminRecord>(`${p}/records/${recordId}/release`, reason ? { reason } : {}),
      /** 拒绝人工放行(理由必填;考生检录页看到理由,可重新拍照再申请到迟到截止) */
      rejectRelease: (recordId: string, reason: string) =>
        api.post<AdminRecord>(`${p}/records/${recordId}/release-reject`, { reason }),
      /** 能当第二确认人的(本机构管理员 + 这个考场的主监考,不含本人) */
      confirmers: (recordId: string) => api.get<Confirmer[]>(`${p}/records/${recordId}/confirmers`),
      /** 给第二确认人发短信验证码(开发环境回显 devCode) */
      confirmCode: (
        recordId: string,
        body: { action: 'force_submit' | 'disqualify'; confirmerAccountId: string },
      ) =>
        api.post<{ resendAfterSec: number; phone: string; devCode?: string }>(
          `${p}/records/${recordId}/confirm-code`,
          body,
        ),
      forceSubmit: (recordId: string, body: { reason: string; confirmerAccountId: string; code: string }) =>
        api.post<AdminRecord>(`${p}/records/${recordId}/force-submit`, body),
      disqualify: (recordId: string, body: { reason: string; confirmerAccountId: string; code: string }) =>
        api.post<AdminRecord>(`${p}/records/${recordId}/disqualify`, body),
      /** 撤销强制交卷 / 取消资格(管理员或本考场主监考;截止已过要同时补时) */
      revoke: (recordId: string, body: { reason: string; extendMin?: number }) =>
        api.post<AdminRecord>(`${p}/records/${recordId}/revoke`, body),
      /** 延长本场:累计将超过 60 分钟时改为提交平台审批(返回 requested) */
      extend: (id: string, body: { minutes: number; reason: string; roomId?: string }) =>
        api.post<{ rooms: number; records: number; requested?: { id: string } }>(
          `${p}/exams/${id}/extend`,
          body,
        ),
      extendRecord: (recordId: string, body: { minutes: number; reason: string }) =>
        api.post<{ rooms: number; records: number; deadlineAt: string | null }>(
          `${p}/records/${recordId}/extend`,
          body,
        ),
      archive: (recordId: string) => api.get<RecordArchive>(`${p}/records/${recordId}/archive`),
      /** 录屏回看:这名考生的录屏片段(按开始时间) */
      screenClips: (recordId: string) => api.get<ScreenClip[]>(`${p}/records/${recordId}/screen-clips`),
      /** ACM 榜单:真实榜 + 封榜视图;fresh = 跳过 10 秒缓存 */
      acmBoard: (id: string, group?: string, fresh = false) =>
        api.get<AcmStaffBoard>(`${p}/exams/${id}/acm-board`, {
          query: { group, fresh: fresh ? '1' : undefined },
        }),
      unfreeze: (id: string) => api.post<{ unfrozenAt: string }>(`${p}/exams/${id}/acm-board/unfreeze`),
      broadcasts: (id: string) => api.get<BroadcastRow[]>(`${p}/exams/${id}/broadcasts`),
      broadcast: (
        id: string,
        body: { text: string; roomId?: string; extendMin: number; kind?: 'notice' | 'judge_delay' },
      ) =>
        api.post<{ notice: ExamNotice; extended: { rooms: number; records: number } }>(
          `${p}/exams/${id}/broadcasts`,
          body,
        ),
    },
    /** 平台 · 考试(平台管理员):归档解锁、延长本场审批 */
    platformExams: {
      archived: (q?: string) =>
        api.get<ArchivedExam[]>('/v1/platform/exams/archived', { query: { q: q || undefined } }),
      unlock: (id: string, reason: string) =>
        api.post<{ id: string; status: string; unlockedUntil: string }>(`/v1/platform/exams/${id}/unlock`, {
          reason,
        }),
      extendRequests: (status?: 'pending' | 'all') =>
        api.get<PlatformExtendRequest[]>('/v1/platform/exams/extend-requests', { query: { status } }),
      decideExtend: (id: string, body: { approve: boolean; note?: string }) =>
        api.post<{ id: string; status: string; rooms: number; records: number }>(
          `/v1/platform/exams/extend-requests/${id}/decide`,
          body,
        ),
    },
    org: {
      classes: () => api.get<OrgClass[]>('/v1/admin/org/classes'),
      members: (classId: string) => api.get<OrgStudent[]>(`/v1/admin/org/classes/${classId}/members`),
      students: (page = 1, size = 200) =>
        api.get<{ items: OrgStudent[]; total: number }>('/v1/admin/org/students', { query: { page, size } }),
    },
    content: {
      papers: () => api.get<Paper[]>('/v1/content/papers'),
    },
    accounts: {
      staff: (role?: string) => api.get<StaffAccount[]>('/v1/admin/accounts', { query: { role } }),
    },
  };
}
export type AdminApi = ReturnType<typeof adminApi>;
